import { useTranslate } from "ra-core";
import { useId } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FIELD_CATALOG,
  LEAD_STATUS_FIELD_PATHS,
  STAGE_FIELD_PATHS,
  type FieldDef,
} from "../../../../supabase/functions/_shared/automation/fields.ts";
import {
  OPS,
  type Trigger,
} from "../../../../supabase/functions/_shared/automation/types.ts";
import { regexIssue, withCurrent } from "./ruleModel";
import { useRowFocus } from "./useRowFocus";
import { isOpenStage } from "../domain/deals/stageModel";
import { useDealStages } from "../deals/useDealStages";
import { useStageLabel } from "../deals/useStageLabel";
import { useLeadStatuses } from "../leads/useLeadStatuses";
import { useLeadStatusLabel } from "../leads/useLeadStatusLabel";

type Leaf = { field: string; op: string; value?: unknown };
type Group = { all?: Node[]; any?: Node[] };
type Node = Leaf | Group;
const isGroup = (n: Node): n is Group =>
  Array.isArray((n as Group).all) || Array.isArray((n as Group).any);
const kidsOf = (g: Group) => g.all ?? g.any ?? [];
const withKids = (g: Group, kids: Node[]): Group =>
  g.any ? { any: kids } : { all: kids };

/** no_activity stage_in: a checklist of the open configured stages; a stored value not among them stays listed and checked. */
const StageChecklist = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) => {
  const t = useTranslate();
  const stages = useDealStages();
  const stageLabel = useStageLabel();
  const options = withCurrent(
    stages.map((s) => s.value).filter(isOpenStage),
    value,
  );
  return (
    <div
      role="group"
      aria-label={t("gavion.automation.editor.stages")}
      className="flex flex-wrap gap-x-4 gap-y-1"
    >
      {options.map((o) => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={value.includes(o)}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? options.filter((x) => x === o || value.includes(x))
                  : value.filter((x) => x !== o),
              )
            }
          />
          {stageLabel(o)}
        </label>
      ))}
    </div>
  );
};

export const selectClass =
  "h-9 rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

const LIST_OPS = new Set(["in", "nin"]);
const NO_VALUE_OPS = new Set(["exists", "not_exists"]);

const showValue = (v: unknown, op: string): string =>
  LIST_OPS.has(op) && Array.isArray(v)
    ? v.join(", ")
    : v !== null && typeof v === "object"
      ? JSON.stringify(v)
      : String(v ?? "");

function parseValue(raw: string, op: string, def?: FieldDef): unknown {
  if (op === "matches") return raw;
  if (raw.startsWith("{") || raw.startsWith("[")) {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  if (LIST_OPS.has(op))
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  if (def?.type === "number" || op === "days_since_gte")
    return raw === "" || Number.isNaN(Number(raw)) ? raw : Number(raw);
  if (def?.type === "boolean") return raw === "true";
  return raw;
}

/** Field picker grouped by entity (spec 05 §7). */
const FieldSelect = ({
  trigger,
  value,
  onChange,
}: {
  trigger: Trigger;
  value: string;
  onChange: (v: string) => void;
}) => {
  const t = useTranslate();
  const fields = FIELD_CATALOG[trigger];
  const groups = [...new Set(fields.map((f) => f.group))];
  const known = fields.some((f) => f.path === value);
  return (
    <select
      aria-label={t("gavion.automation.editor.field")}
      className={selectClass}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {!known && <option value={value}>{value}</option>}
      {groups.map((g) => (
        <optgroup key={g} label={t(`gavion.automation.field_groups.${g}`)}>
          {fields
            .filter((f) => f.group === g)
            .map((f) => (
              <option key={f.path} value={f.path}>
                {f.path}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
};

const LeafRow = ({
  trigger,
  leaf,
  n,
  onChange,
  onRemove,
}: {
  trigger: Trigger;
  leaf: Leaf;
  n: string;
  onChange: (l: Leaf) => void;
  onRemove: () => void;
}) => {
  const t = useTranslate();
  const label = t("gavion.automation.editor.condition_n", { n });
  const errId = useId();
  const def = FIELD_CATALOG[trigger].find((f) => f.path === leaf.field);
  const regex = leaf.op === "matches" ? regexIssue(leaf.value ?? "") : null;
  const stages = useDealStages();
  const stageLabel = useStageLabel();
  const statuses = useLeadStatuses(); // GAVION lead statuses
  const statusLabel = useLeadStatusLabel();
  const isStageField = !!def && STAGE_FIELD_PATHS.includes(def.path);
  const isStatusField = !!def && LEAD_STATUS_FIELD_PATHS.includes(def.path);
  const current = typeof leaf.value === "string" ? [leaf.value] : [];
  const enumValues = isStageField
    ? withCurrent(
        stages.map((s) => s.value),
        current,
      )
    : isStatusField
      ? withCurrent(
          statuses.map((s) => s.value),
          current,
        )
      : def?.enumValues;
  const enumSelect = enumValues && (leaf.op === "eq" || leaf.op === "neq");
  return (
    <div
      role="group"
      aria-label={label}
      data-row=""
      className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start"
    >
      <FieldSelect
        trigger={trigger}
        value={leaf.field}
        onChange={(field) => onChange({ ...leaf, field })}
      />
      <select
        aria-label={t("gavion.automation.editor.op")}
        className={selectClass}
        value={leaf.op}
        onChange={(e) => onChange({ ...leaf, op: e.target.value })}
      >
        {OPS.map((o) => (
          <option key={o} value={o}>
            {t(`gavion.automation.ops.${o}`)}
          </option>
        ))}
      </select>
      {NO_VALUE_OPS.has(leaf.op) ? null : enumSelect ? (
        <select
          aria-label={t("gavion.automation.editor.value")}
          className={selectClass}
          value={String(leaf.value ?? "")}
          onChange={(e) => onChange({ ...leaf, value: e.target.value })}
        >
          <option value="" />
          {enumValues!.map((v) => (
            <option key={v} value={v}>
              {isStageField
                ? stageLabel(v)
                : isStatusField
                  ? statusLabel(v)
                  : v}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex flex-col gap-1">
          <Input
            aria-label={t("gavion.automation.editor.value")}
            aria-invalid={regex ? true : undefined}
            aria-describedby={regex ? errId : undefined}
            className="w-full sm:w-56"
            placeholder={
              LIST_OPS.has(leaf.op)
                ? t("gavion.automation.editor.value_list_hint")
                : undefined
            }
            value={showValue(leaf.value, leaf.op)}
            onChange={(e) =>
              onChange({
                ...leaf,
                value: parseValue(e.target.value, leaf.op, def),
              })
            }
          />
          {regex && (
            <p id={errId} role="alert" className="text-xs text-destructive">
              {t(`gavion.automation.errors.${regex}`)}
            </p>
          )}
        </div>
      )}
      <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
        {t("gavion.automation.editor.remove_condition", { n })}
      </Button>
    </div>
  );
};

/** `n` numbers nested rows ("3.1"); empty for the root group. */
const GroupBox = ({
  trigger,
  group,
  n,
  onChange,
  onRemove,
}: {
  trigger: Trigger;
  group: Group;
  n: string;
  onChange: (g: Group) => void;
  onRemove?: () => void;
}) => {
  const t = useTranslate();
  const depth = n ? 1 : 0;
  const label = n
    ? t("gavion.automation.editor.group_n", { n })
    : t("gavion.automation.editor.conditions");
  const childN = (i: number) => (n ? `${n}.${i + 1}` : String(i + 1));
  const kids = kidsOf(group);
  const replace = (i: number, n: Node | null) =>
    onChange(
      withKids(
        group,
        n === null
          ? kids.filter((_, j) => j !== i)
          : kids.map((k, j) => (j === i ? n : k)),
      ),
    );
  const firstField = FIELD_CATALOG[trigger][0]?.path ?? "";
  const focus = useRowFocus();
  const removeAt = (i: number) => {
    focus.afterRemove(i, kids.length - 1);
    replace(i, null);
  };
  const append = (node: Node) => {
    focus.afterAdd(kids.length);
    onChange(withKids(group, [...kids, node]));
  };
  return (
    <div
      ref={focus.containerRef}
      role="group"
      aria-label={label}
      data-row={n ? "" : undefined}
      className="flex flex-col gap-3 rounded-lg border p-3"
    >
      <select
        aria-label={`${label} — ${t("gavion.automation.editor.group_mode")}`}
        className={`${selectClass} w-full sm:w-64`}
        value={group.any ? "any" : "all"}
        onChange={(e) =>
          onChange(e.target.value === "any" ? { any: kids } : { all: kids })
        }
      >
        <option value="all">{t("gavion.automation.editor.all")}</option>
        <option value="any">{t("gavion.automation.editor.any")}</option>
      </select>
      {kids.map((k, i) =>
        isGroup(k) ? (
          <GroupBox
            key={i}
            trigger={trigger}
            group={k}
            n={childN(i)}
            onChange={(g) => replace(i, g)}
            onRemove={() => removeAt(i)}
          />
        ) : (
          <LeafRow
            key={i}
            trigger={trigger}
            leaf={k as Leaf}
            n={childN(i)}
            onChange={(l) => replace(i, l)}
            onRemove={() => removeAt(i)}
          />
        ),
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          ref={focus.addRef}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ field: firstField, op: "eq", value: "" })}
        >
          {t("gavion.automation.editor.add_condition")}
        </Button>
        {depth < 1 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ any: [] })}
          >
            {t("gavion.automation.editor.add_group")}
          </Button>
        )}
        {onRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            {t("gavion.automation.editor.remove_group", { n })}
          </Button>
        )}
      </div>
    </div>
  );
};

/** All/any groups up to 2 levels (spec 05 §7); special editors for no_activity and schedule. */
export const ConditionBuilder = ({
  trigger,
  value,
  onChange,
}: {
  trigger: Trigger;
  value: unknown;
  onChange: (v: unknown) => void;
}) => {
  const t = useTranslate();
  const v = (value ?? {}) as Record<string, unknown>;
  if (trigger === "no_activity") {
    const stages = Array.isArray(v.stage_in) ? (v.stage_in as string[]) : [];
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <select
          aria-label={t("gavion.automation.editor.no_activity_entity")}
          className={selectClass}
          value={String(v.entity ?? "deal")}
          onChange={(e) => onChange({ ...v, entity: e.target.value })}
        >
          <option value="deal">deal</option>
          <option value="company">company</option>
        </select>
        <Input
          aria-label={t("gavion.automation.editor.no_activity_days")}
          type="number"
          min={1}
          className="w-full sm:w-24"
          value={Number(v.days ?? 7)}
          onChange={(e) => onChange({ ...v, days: Number(e.target.value) })}
        />
        <StageChecklist
          value={stages}
          onChange={(stage_in) => onChange({ ...v, stage_in })}
        />
      </div>
    );
  }
  if (trigger === "schedule") {
    return (
      <Input
        aria-label={t("gavion.automation.editor.cron")}
        className="w-full sm:w-56"
        value={String(v.cron ?? "")}
        onChange={(e) => onChange({ cron: e.target.value })}
      />
    );
  }
  // A lone leaf is shown inside an implicit "all" group; `{}` ("always") is shown as an empty group.
  const root: Group =
    "field" in v
      ? { all: [v as unknown as Leaf] }
      : isGroup(v as Group)
        ? (v as Group)
        : { all: [] };
  return <GroupBox trigger={trigger} group={root} n="" onChange={onChange} />;
};
